import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient, API_BASE_URL, WabaAccount } from '../../services/api';
import { getQualityRatingColor, getQualityRatingLabel } from '../../utils/statusColors';
import type {
  TenantBillingData,
  TenantDropdownItem,
  Invoice,
} from '../../types/billing';
import type {
  StatisticsSummary,
  TimeSeriesResponse,
  TopTemplatesResponse,
  ErrorBreakdownResponse,
  QualityHistoryResponse,
  StatisticsPeriod,
} from '../../types/statistics';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ChartTooltipPayload = { dataKey?: string; value?: number; color?: string };
import {
  ArrowLeft,
  RefreshCw,
  Loader2,
  DollarSign,
  Send,
  CheckCircle2,
  Eye,
  XCircle,
  Phone,
  TrendingUp,
  AlertCircle,
  AlertTriangle,
  Calendar,
  Clock,
  Users,
  Gift,
  ChevronDown,
  FileText,
  X,
  Star,
  BarChart3,
  Layers,
  ShieldAlert,
} from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

// ============================================
// CONSTANTES
// ============================================

const statusLabels: Record<string, string> = {
  draft: 'Rascunho',
  pending: 'Aguardando',
  billed: 'Faturado',
  paid: 'Pago',
  overdue: 'Vencido',
  cancelled: 'Cancelado',
};

const statusColors: Record<string, string> = {
  draft: 'bg-bg-3 text-text-2',
  pending: 'bg-warning/10 text-warning',
  billed: 'bg-info/10 text-accent-1',
  paid: 'bg-success/10 text-success',
  overdue: 'bg-danger/10 text-danger',
  cancelled: 'bg-bg-3 text-text-3',
};

type DetailTab = 'waba' | 'templates' | 'users' | 'errors' | 'quality';

// ============================================
// CHART CONFIG
// ============================================

const chartSeriesConfig: Record<string, { label: string; colorVar: string; dotClass: string }> = {
  sentMessages: { label: 'Enviadas', colorVar: '--chart-sent', dotClass: 'bg-[var(--chart-sent)]' },
  deliveredMessages: { label: 'Entregues', colorVar: '--chart-delivered', dotClass: 'bg-[var(--chart-delivered)]' },
  readMessages: { label: 'Lidas', colorVar: '--chart-read', dotClass: 'bg-[var(--chart-read)]' },
  failedMessages: { label: 'Falhas', colorVar: '--chart-failed', dotClass: 'bg-[var(--chart-failed)]' },
  cost: { label: 'Custo', colorVar: '--chart-cost', dotClass: 'bg-[var(--chart-cost)]' },
};

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: ChartTooltipPayload[]; label?: string }) {
  if (!active || !payload || payload.length === 0) return null;

  const fmtNum = (v: number) => new Intl.NumberFormat('pt-BR').format(v);
  const fmtCur = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  // Deduplica: se o mesmo dataKey aparece mais de uma vez (Area+Line), mostra só 1
  const seen = new Set<string>();

  return (
    <div
      style={{
        backgroundColor: 'var(--chart-tooltip-bg)',
        border: '1px solid var(--chart-tooltip-border)',
        color: 'var(--chart-tooltip-text)',
      }}
      className="rounded-xl shadow-2xl px-4 py-3 min-w-[180px]"
    >
      <p
        className="text-sm font-semibold mb-2 pb-2"
        style={{ borderBottom: '1px solid var(--chart-tooltip-border)', color: 'var(--chart-tooltip-text)' }}
      >
        {label}
      </p>
      <div className="space-y-1.5">
        {payload.map((entry) => {
          const key = entry.dataKey as string;
          const config = chartSeriesConfig[key];
          if (!config || seen.has(key)) return null;
          seen.add(key);

          const value = entry.value ?? 0;
          const formatted = key === 'cost' ? fmtCur(value) : fmtNum(value);

          return (
            <div key={key} className="flex items-center justify-between gap-4 text-sm">
              <span className="flex items-center gap-2" style={{ color: 'var(--chart-tooltip-muted)' }}>
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: `var(${config.colorVar})` }}
                />
                {config.label}
              </span>
              <span className="font-semibold tabular-nums" style={{ color: 'var(--chart-tooltip-text)' }}>
                {formatted}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export default function UnifiedDashboard() {
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  // --- Seletores ---
  const [tenants, setTenants] = useState<TenantDropdownItem[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [wabaAccounts, setWabaAccounts] = useState<WabaAccount[]>([]);
  const [selectedWabaId, setSelectedWabaId] = useState<string>('all');

  // --- Dados de billing (agregado, ciclo atual) ---
  const [billing, setBilling] = useState<TenantBillingData | null>(null);
  const [loadingBilling, setLoadingBilling] = useState(true);

  // --- Dados per-WABA (quando uma conta específica é selecionada) ---
  const [wabaSummary, setWabaSummary] = useState<StatisticsSummary | null>(null);
  const [wabaTimeSeries, setWabaTimeSeries] = useState<TimeSeriesResponse | null>(null);
  const [wabaTopTemplates, setWabaTopTemplates] = useState<TopTemplatesResponse | null>(null);
  const [wabaErrors, setWabaErrors] = useState<ErrorBreakdownResponse | null>(null);
  const [wabaQuality, setWabaQuality] = useState<QualityHistoryResponse | null>(null);
  const [loadingWaba, setLoadingWaba] = useState(false);

  // --- Filtros per-WABA ---
  const [period, setPeriod] = useState<StatisticsPeriod>('daily');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // --- UI state ---
  const [activeTab, setActiveTab] = useState<DetailTab>('waba');
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inicializar datas padrão (últimos 30 dias)
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  }, []);

  // Buscar tenants (Super Admin) ou billing direto
  useEffect(() => {
    if (isSuperAdmin) {
      fetchTenants();
    } else {
      fetchBilling();
      fetchWabaAccounts();
    }
  }, [isSuperAdmin]);

  // Quando tenant muda (Super Admin)
  useEffect(() => {
    if (selectedTenantId) {
      fetchBilling(selectedTenantId);
      fetchWabaAccounts(selectedTenantId);
    }
  }, [selectedTenantId]);

  // Quando WABA selecionada muda
  useEffect(() => {
    if (selectedWabaId !== 'all' && startDate && endDate) {
      fetchWabaData(selectedWabaId);
    }
  }, [selectedWabaId, period, startDate, endDate]);

  // ============================================
  // FETCH FUNCTIONS
  // ============================================

  const fetchTenants = async () => {
    try {
      if (!accessToken) return;
      const response = await apiClient.getBillingTenantsDropdown(accessToken);
      if (response.success && response.data) {
        setTenants(response.data);
        if (response.data.length > 0) {
          setSelectedTenantId(response.data[0].id);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar tenants');
      setLoadingBilling(false);
    }
  };

  const fetchBilling = async (tenantId?: string) => {
    setLoadingBilling(true);
    setError(null);
    try {
      if (!accessToken) return;
      const response = await apiClient.getTenantBilling(accessToken, tenantId);
      if (response.success && response.data) {
        setBilling(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoadingBilling(false);
    }
  };

  const fetchWabaAccounts = async (tenantId?: string) => {
    try {
      if (!accessToken) return;
      const response = await apiClient.getWabaAccounts(accessToken, tenantId);
      if (response.success) {
        setWabaAccounts(response.data.wabaAccounts || []);
      }
    } catch {
      // Silencioso - as contas WABA são opcionais para a visão agregada
    }
  };

  const fetchWabaData = useCallback(async (wabaId: string) => {
    if (!accessToken || !startDate || !endDate) return;
    setLoadingWaba(true);
    const token = localStorage.getItem('accessToken');
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

    try {
      // Fetch tudo em paralelo
      const [summaryRes, timeSeriesRes, templatesRes, errorsRes, qualityRes] = await Promise.allSettled([
        fetch(`${API_BASE_URL}/api/waba/${wabaId}/statistics/summary?startDate=${startDate}&endDate=${endDate}`, { headers }),
        fetch(`${API_BASE_URL}/api/waba/${wabaId}/statistics/timeseries?period=${period}&startDate=${startDate}&endDate=${endDate}`, { headers }),
        fetch(`${API_BASE_URL}/api/waba/${wabaId}/statistics/top-templates?limit=10`, { headers }),
        fetch(`${API_BASE_URL}/api/waba/${wabaId}/statistics/errors`, { headers }),
        fetch(`${API_BASE_URL}/api/waba/${wabaId}/quality-history`, { headers }),
      ]);

      // Summary
      if (summaryRes.status === 'fulfilled' && summaryRes.value.ok) {
        const data = await summaryRes.value.json();
        const s = data.data?.statistics || data.statistics || {};
        setWabaSummary({
          totalMessages: s.totalMessagesSent || 0,
          sentMessages: s.totalMessagesSent || 0,
          deliveredMessages: s.messagesDelivered || 0,
          readMessages: s.messagesRead || 0,
          failedMessages: s.messagesFailed || 0,
          deliveryRate: s.deliveryRate || 0,
          readRate: s.readRate || 0,
          totalCost: s.totalCostBRL ? Math.round(s.totalCostBRL * 100) : 0,
          averageCostPerMessage: s.avgCostPerConversation ? Math.round(s.avgCostPerConversation * 100) : 0,
          period: { start: startDate, end: endDate, periodType: period },
        });
      }

      // Time Series
      if (timeSeriesRes.status === 'fulfilled' && timeSeriesRes.value.ok) {
        const data = await timeSeriesRes.value.json();
        const ts = data.data?.timeSeries || data.timeSeries || [];
        setWabaTimeSeries({
          dataPoints: ts.map((p: any) => ({
            date: p.period,
            sentMessages: p.messagesSent || 0,
            deliveredMessages: p.messagesDelivered || 0,
            readMessages: p.messagesRead || 0,
            failedMessages: p.messagesFailed || 0,
            deliveryRate: p.deliveryRate || 0,
            readRate: p.readRate || 0,
            cost: p.cost || 0,
          })),
          period: { start: startDate, end: endDate, periodType: period },
        });
      }

      // Top Templates
      if (templatesRes.status === 'fulfilled' && templatesRes.value.ok) {
        const data = await templatesRes.value.json();
        const tt = data.data?.topTemplates || data.topTemplates || [];
        setWabaTopTemplates({
          templates: tt.map((t: any) => ({
            templateId: t.templateName || '',
            templateName: t.templateName || '',
            category: t.category || 'UTILITY',
            totalSent: t.usageCount || 0,
            totalDelivered: Math.round((t.deliveryRate / 100) * (t.usageCount || 0)),
            totalRead: Math.round((t.readRate / 100) * (t.usageCount || 0)),
            totalFailed: 0,
            deliveryRate: t.deliveryRate || 0,
            readRate: t.readRate || 0,
            totalCost: Math.round((t.totalCost || 0) * 100),
            averageCostPerMessage: t.usageCount > 0 ? Math.round(((t.totalCost || 0) / t.usageCount) * 100) : 0,
          })),
          limit: 10,
        });
      }

      // Errors
      if (errorsRes.status === 'fulfilled' && errorsRes.value.ok) {
        const data = await errorsRes.value.json();
        const eb = data.data?.errorBreakdown || data.errorBreakdown || [];
        const totalErrors = eb.reduce((sum: number, e: any) => sum + (e.count || 0), 0);
        setWabaErrors({
          totalErrors,
          errors: eb.map((e: any) => ({
            errorCode: e.errorType || 'UNKNOWN',
            errorType: e.errorType || 'Unknown Error',
            description: e.errorType || 'Unknown error',
            count: e.count || 0,
            percentage: e.percentage || 0,
            affectedMessages: e.count || 0,
          })),
          period: { start: startDate, end: endDate },
        });
      }

      // Quality
      if (qualityRes.status === 'fulfilled' && qualityRes.value.ok) {
        const data = await qualityRes.value.json();
        setWabaQuality(data.data || data);
      }
    } catch {
      // Os erros individuais são tratados silenciosamente
    } finally {
      setLoadingWaba(false);
    }
  }, [accessToken, startDate, endDate, period]);

  const fetchInvoiceDetails = async (invoiceId: string) => {
    if (!accessToken) return;
    setLoadingInvoice(true);
    try {
      const response = await apiClient.getInvoice(invoiceId, accessToken, true);
      if (response.success && response.data) {
        setViewingInvoice(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar fatura');
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleRefresh = () => {
    fetchBilling(selectedTenantId || undefined);
    if (selectedWabaId !== 'all') {
      fetchWabaData(selectedWabaId);
    }
  };

  const handleWabaSelect = (wabaId: string) => {
    setSelectedWabaId(wabaId);
    // Limpar dados antigos per-WABA
    if (wabaId === 'all') {
      setWabaSummary(null);
      setWabaTimeSeries(null);
      setWabaTopTemplates(null);
      setWabaErrors(null);
      setWabaQuality(null);
    }
  };

  // ============================================
  // FORMATAÇÃO
  // ============================================

  const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const formatCurrencyCents = (value: number): string =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }).format(value / 100);

  const formatNumber = (value: number): string =>
    new Intl.NumberFormat('pt-BR').format(value);

  const formatPercentage = (value: number): string =>
    `${value.toFixed(1)}%`;

  const formatShortDate = (date: string): string =>
    new Date(date + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });

  const formatDate = (date: string): string =>
    new Date(date + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const formatPeriod = (start: string, end: string): string =>
    `${formatShortDate(start)} - ${formatShortDate(end)}`;

  const getPeriodLabel = (p: StatisticsPeriod): string => {
    const map: Record<string, string> = { daily: 'Diário', weekly: 'Semanal', monthly: 'Mensal', total: 'Total' };
    return map[p] || p;
  };

  // Dados derivados
  const isAllView = selectedWabaId === 'all';
  const selectedWabaAccount = wabaAccounts.find(a => a.id === selectedWabaId);
  const isLoading = loadingBilling || (loadingWaba && !isAllView);

  // Dados de KPI adaptados ao contexto
  const kpiSent = isAllView ? billing?.currentMetrics.totalDispatches : wabaSummary?.sentMessages;
  const kpiDelivered = isAllView ? billing?.currentMetrics.successfulDispatches : wabaSummary?.deliveredMessages;
  const kpiRead = isAllView ? undefined : wabaSummary?.readMessages;
  const kpiReadRate = isAllView ? billing?.currentMetrics.readRate : wabaSummary?.readRate;
  const kpiFailed = isAllView ? billing?.currentMetrics.failedDispatches : wabaSummary?.failedMessages;
  const kpiDeliveryRate = isAllView ? billing?.currentMetrics.deliveryRate : wabaSummary?.deliveryRate;

  // Dados do gráfico adaptados
  const chartData = isAllView
    ? (billing?.dailyStats || []).map(d => ({
        label: new Date(d.date + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        sentMessages: d.dispatches,
        deliveredMessages: d.delivered,
        readMessages: d.read,
        failedMessages: d.failed,
        cost: d.cost,
      }))
    : (wabaTimeSeries?.dataPoints || []).map(p => ({
        label: new Date(p.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        sentMessages: p.sentMessages,
        deliveredMessages: p.deliveredMessages,
        readMessages: p.readMessages,
        failedMessages: p.failedMessages,
        cost: p.cost,
      }));

  // Tabs — todas sempre clicáveis
  const tabs: { id: DetailTab; label: string; icon: React.ReactNode }[] = [
    { id: 'waba', label: 'Contas WABA', icon: <Phone size={16} /> },
    { id: 'templates', label: 'Templates', icon: <Layers size={16} /> },
    { id: 'users', label: 'Usuários', icon: <Users size={16} /> },
    { id: 'errors', label: 'Erros', icon: <ShieldAlert size={16} /> },
    { id: 'quality', label: 'Qualidade', icon: <Star size={16} /> },
  ];

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="min-h-screen bg-bg-1">
      <Header
        title="Painel de Operações"
        subtitle="Visão unificada de métricas, custos e qualidade"
      >
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center gap-2 px-4 py-2 text-text-2 hover:text-text-1 hover:bg-bg-3 rounded-lg transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="hidden sm:inline">Voltar</span>
        </button>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          <span className="hidden sm:inline">Atualizar</span>
        </button>
      </Header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* ======== SELETORES ======== */}
        <div className="flex flex-col gap-4 mb-6">
          {/* Tenant Selector (Super Admin) */}
          {isSuperAdmin && tenants.length > 0 && (
            <div className="bg-bg-2 p-4 rounded-lg border border-border-1">
              <label className="block text-sm font-medium text-text-2 mb-2">Tenant</label>
              <div className="relative">
                <select
                  value={selectedTenantId || ''}
                  onChange={(e) => { setSelectedTenantId(e.target.value); setSelectedWabaId('all'); }}
                  className="w-full md:w-80 appearance-none bg-bg-3 border border-border-1 rounded-lg px-4 py-2 pr-10 text-text-1 focus:outline-none focus:border-accent-1"
                >
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <ChevronDown size={20} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-3 pointer-events-none" />
              </div>
            </div>
          )}

          {/* WABA Account Selector (Dropdown) */}
          <div className="flex flex-wrap items-center gap-4">
            <label className="text-sm font-medium text-text-3">Conta WABA:</label>
            <div className="relative">
              <select
                value={selectedWabaId}
                onChange={(e) => handleWabaSelect(e.target.value)}
                className="appearance-none bg-bg-2 border border-border-1 rounded-lg px-4 py-2.5 pr-10 text-sm text-text-1 focus:outline-none focus:border-accent-1 min-w-[340px] transition-colors hover:border-accent-1/50"
              >
                <option value="all">Todas as contas ({wabaAccounts.length})</option>
                {wabaAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.displayPhoneNumber} — {account.businessName || 'Sem nome'}
                  </option>
                ))}
              </select>
              <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-3 pointer-events-none" />
            </div>
            {/* Badge com info da conta selecionada */}
            {selectedWabaAccount && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-accent-1/10 rounded-lg border border-accent-1/20">
                <Phone size={14} className="text-accent-1" />
                <span className="text-sm font-medium text-accent-1">{selectedWabaAccount.displayPhoneNumber}</span>
                {selectedWabaAccount.businessName && (
                  <span className="text-xs text-text-2">{selectedWabaAccount.businessName}</span>
                )}
                {selectedWabaAccount.qualityRating && (
                  <span className={`text-xs font-medium ${getQualityRatingColor(selectedWabaAccount.qualityRating)}`}>
                    {getQualityRatingLabel(selectedWabaAccount.qualityRating)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Date range + Period (apenas quando WABA específica) */}
          {!isAllView && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-text-3" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-1.5 bg-bg-2 border border-border-1 text-text-1 rounded-lg text-sm focus:outline-none focus:border-accent-1"
                />
                <span className="text-text-3 text-sm">até</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-1.5 bg-bg-2 border border-border-1 text-text-1 rounded-lg text-sm focus:outline-none focus:border-accent-1"
                />
              </div>
              <div className="flex items-center gap-1 bg-bg-2 border border-border-1 rounded-lg p-0.5">
                {(['daily', 'weekly', 'monthly'] as StatisticsPeriod[]).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      period === p ? 'bg-accent-1 text-white' : 'text-text-2 hover:text-text-1 hover:bg-bg-3'
                    }`}
                  >
                    {getPeriodLabel(p)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-danger/10 border border-danger/30 rounded-lg flex items-center gap-3">
            <AlertCircle className="text-danger flex-shrink-0" size={20} />
            <span className="text-danger text-sm">{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loadingBilling && !billing && <LoadingSpinner size="lg" />}

        {/* ======== CONTEÚDO PRINCIPAL ======== */}
        {billing && (
          <>
            {/* ======== BILLING STRIP (apenas visão "Todas") ======== */}
            {isAllView && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                {/* Ciclo Atual */}
                <div className="bg-bg-2 p-4 rounded-lg border border-border-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar size={16} className="text-accent-1" />
                    <span className="text-xs text-text-3 uppercase tracking-wider">Ciclo</span>
                  </div>
                  <p className="text-sm font-semibold text-text-1">
                    {formatPeriod(billing.currentPeriod.periodStart, billing.currentPeriod.periodEnd)}
                  </p>
                  <p className="text-xs text-text-3 mt-1">Fechamento dia {billing.billingCycleDay}</p>
                </div>

                {/* Dias Restantes */}
                <div className="bg-bg-2 p-4 rounded-lg border border-border-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock size={16} className="text-accent-1" />
                    <span className="text-xs text-text-3 uppercase tracking-wider">Progresso</span>
                  </div>
                  <p className="text-sm font-semibold text-text-1">
                    {billing.currentPeriod.daysRemaining} dias restantes
                  </p>
                  <div className="w-full bg-bg-3 rounded-full h-1.5 mt-2">
                    <div
                      className="bg-accent-1 h-1.5 rounded-full transition-all"
                      style={{ width: `${(billing.currentPeriod.daysElapsed / billing.currentPeriod.daysTotal) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Custo Total */}
                <div className="bg-bg-2 p-4 rounded-lg border border-border-1">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign size={16} className="text-success" />
                    <span className="text-xs text-text-3 uppercase tracking-wider">Custo Ciclo</span>
                  </div>
                  <p className="text-sm font-bold text-success">{formatCurrency(billing.currentCosts.totalCost)}</p>
                  <div className="flex gap-3 mt-1 text-xs text-text-3">
                    <span>U: {formatNumber(billing.currentCosts.utilityCount)}</span>
                    <span>M: {formatNumber(billing.currentCosts.marketingCount)}</span>
                    <span className="flex items-center gap-0.5"><Gift size={10} /> {formatNumber(billing.currentMetrics.freeDispatches)}</span>
                  </div>
                </div>

                {/* Projeção */}
                <div className="bg-bg-2 p-4 rounded-lg border border-border-1">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp size={16} className="text-accent-2" />
                    <span className="text-xs text-text-3 uppercase tracking-wider">Projeção</span>
                  </div>
                  <p className="text-sm font-bold text-text-1">{formatCurrency(billing.currentCosts.estimatedMonthEnd)}</p>
                  <p className="text-xs text-text-3 mt-1">Estimativa fim do ciclo</p>
                </div>
              </div>
            )}

            {/* ======== KPI CARDS ======== */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              {/* Enviadas */}
              <div className="bg-bg-2 p-5 rounded-lg border border-border-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-info/10 rounded-lg">
                    <Send size={18} className="text-accent-1" />
                  </div>
                  {loadingWaba && !isAllView && <Loader2 size={14} className="animate-spin text-text-3" />}
                </div>
                <p className="text-xs text-text-3 mb-1">{isAllView ? 'Total Disparos' : 'Enviadas'}</p>
                <p className="text-2xl font-bold text-text-1">
                  {kpiSent != null ? formatNumber(kpiSent) : '-'}
                </p>
              </div>

              {/* Entregues */}
              <div className="bg-bg-2 p-5 rounded-lg border border-border-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-success/10 rounded-lg">
                    <CheckCircle2 size={18} className="text-success" />
                  </div>
                  {kpiDeliveryRate != null && kpiDeliveryRate > 0 && (
                    <div className="flex items-center gap-1 text-success">
                      <TrendingUp size={12} />
                      <span className="text-xs font-medium">{formatPercentage(kpiDeliveryRate)}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-text-3 mb-1">Entregues</p>
                <p className="text-2xl font-bold text-text-1">
                  {kpiDelivered != null ? formatNumber(kpiDelivered) : '-'}
                </p>
              </div>

              {/* Lidas / Taxa Leitura */}
              <div className="bg-bg-2 p-5 rounded-lg border border-border-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-accent-2/10 rounded-lg">
                    <Eye size={18} className="text-accent-2" />
                  </div>
                  {kpiReadRate != null && kpiReadRate > 0 && (
                    <div className="flex items-center gap-1 text-accent-2">
                      <TrendingUp size={12} />
                      <span className="text-xs font-medium">{formatPercentage(kpiReadRate)}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-text-3 mb-1">{isAllView ? 'Taxa Leitura' : 'Lidas'}</p>
                <p className="text-2xl font-bold text-text-1">
                  {isAllView
                    ? (kpiReadRate != null ? formatPercentage(kpiReadRate) : '-')
                    : (kpiRead != null ? formatNumber(kpiRead) : '-')
                  }
                </p>
              </div>

              {/* Falhas */}
              <div className="bg-bg-2 p-5 rounded-lg border border-border-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-danger/10 rounded-lg">
                    <XCircle size={18} className="text-danger" />
                  </div>
                </div>
                <p className="text-xs text-text-3 mb-1">Falhas</p>
                <p className="text-2xl font-bold text-text-1">
                  {kpiFailed != null ? formatNumber(kpiFailed) : '-'}
                </p>
              </div>
            </div>

            {/* ======== CUSTO (per-WABA view) ======== */}
            {!isAllView && wabaSummary && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                <div className="bg-bg-2 p-5 rounded-lg border border-border-1">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="p-2 bg-success/10 rounded-lg">
                      <DollarSign size={18} className="text-success" />
                    </div>
                    <span className="text-sm text-text-3">Custo Total</span>
                  </div>
                  <p className="text-2xl font-bold text-text-1 mt-2">{formatCurrencyCents(wabaSummary.totalCost)}</p>
                </div>
                <div className="bg-bg-2 p-5 rounded-lg border border-border-1">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="p-2 bg-warning/10 rounded-lg">
                      <BarChart3 size={18} className="text-warning" />
                    </div>
                    <span className="text-sm text-text-3">Custo Médio / Mensagem</span>
                  </div>
                  <p className="text-2xl font-bold text-text-1 mt-2">{formatCurrencyCents(wabaSummary.averageCostPerMessage)}</p>
                </div>
              </div>
            )}

            {/* ======== GRÁFICO ======== */}
            {chartData.length > 0 && (
              <div className="bg-bg-2 p-6 rounded-xl border border-border-1 mb-6 relative overflow-hidden">
                {/* Top accent bar */}
                <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: 'linear-gradient(90deg, var(--chart-sent), var(--chart-delivered), var(--chart-cost))' }} />

                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-base font-semibold text-text-1">
                      {isAllView ? 'Disparos e Custos Diários' : 'Volume de Mensagens'}
                    </h3>
                    <p className="text-xs text-text-3 mt-0.5">
                      {isAllView
                        ? `${chartData.length} dias no ciclo atual`
                        : `Período: ${startDate ? formatShortDate(startDate) : ''} — ${endDate ? formatShortDate(endDate) : ''}`
                      }
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Legend pills */}
                    <div className="hidden sm:flex items-center gap-3">
                      <span className="flex items-center gap-1.5 text-xs text-text-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: 'var(--chart-sent)' }} />Enviadas
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-text-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: 'var(--chart-delivered)' }} />Entregues
                      </span>
                      {!isAllView && (
                        <>
                          <span className="flex items-center gap-1.5 text-xs text-text-2">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: 'var(--chart-read)' }} />Lidas
                          </span>
                          <span className="flex items-center gap-1.5 text-xs text-text-2">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: 'var(--chart-failed)' }} />Falhas
                          </span>
                        </>
                      )}
                      {isAllView && (
                        <span className="flex items-center gap-1.5 text-xs text-text-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: 'var(--chart-cost)' }} />Custo
                        </span>
                      )}
                    </div>
                    {(loadingWaba && !isAllView) && <Loader2 size={16} className="animate-spin text-text-3" />}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={340}>
                  <ComposedChart data={chartData} margin={{ top: 8, right: isAllView ? 55 : 12, left: 4, bottom: 4 }}>
                    <defs>
                      <linearGradient id="gradSent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--chart-sent)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--chart-sent)" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="gradDelivered" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--chart-delivered)" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="var(--chart-delivered)" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="gradRead" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--chart-read)" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="var(--chart-read)" stopOpacity={0.01} />
                      </linearGradient>
                      <linearGradient id="gradCost" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--chart-cost)" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="var(--chart-cost)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" opacity={0.5} vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: 'var(--chart-text)' }}
                      tickLine={false}
                      axisLine={{ stroke: 'var(--chart-axis)', strokeOpacity: 0.6 }}
                    />
                    <YAxis
                      yAxisId="msgs"
                      tick={{ fontSize: 11, fill: 'var(--chart-text)' }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)}
                      width={45}
                    />
                    {isAllView && (
                      <YAxis
                        yAxisId="cost"
                        orientation="right"
                        tick={{ fontSize: 11, fill: 'var(--chart-text)' }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v: number) => `R$${v.toFixed(0)}`}
                        width={60}
                      />
                    )}
                    <Tooltip
                      content={(props: any) => <ChartTooltip active={props.active} payload={props.payload} label={props.label} />}
                      cursor={{ stroke: 'var(--chart-sent)', strokeOpacity: 0.3, strokeDasharray: '4 4' }}
                    />

                    {/* Enviadas — area + line */}
                    <Area
                      yAxisId="msgs"
                      type="monotone"
                      dataKey="sentMessages"
                      fill="url(#gradSent)"
                      stroke="var(--chart-sent)"
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 5, fill: 'var(--chart-sent)', stroke: 'var(--chart-tooltip-bg)', strokeWidth: 2 }}
                    />

                    {/* Entregues — area + line */}
                    <Area
                      yAxisId="msgs"
                      type="monotone"
                      dataKey="deliveredMessages"
                      fill="url(#gradDelivered)"
                      stroke="var(--chart-delivered)"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: 'var(--chart-delivered)', stroke: 'var(--chart-tooltip-bg)', strokeWidth: 2 }}
                    />

                    {/* Per-WABA: Lidas + Falhas */}
                    {!isAllView && (
                      <>
                        <Area
                          yAxisId="msgs"
                          type="monotone"
                          dataKey="readMessages"
                          fill="url(#gradRead)"
                          stroke="var(--chart-read)"
                          strokeWidth={1.5}
                          strokeDasharray="5 3"
                          dot={false}
                          activeDot={{ r: 4, fill: 'var(--chart-read)', stroke: 'var(--chart-tooltip-bg)', strokeWidth: 2 }}
                        />
                        <Line
                          yAxisId="msgs"
                          type="monotone"
                          dataKey="failedMessages"
                          stroke="var(--chart-failed)"
                          strokeWidth={1.5}
                          dot={{ r: 2, fill: 'var(--chart-failed)' }}
                          activeDot={{ r: 5, fill: 'var(--chart-failed)', stroke: 'var(--chart-tooltip-bg)', strokeWidth: 2 }}
                        />
                      </>
                    )}

                    {/* All view: Custo — area + line */}
                    {isAllView && (
                      <>
                        <Area
                          yAxisId="cost"
                          type="monotone"
                          dataKey="cost"
                          fill="url(#gradCost)"
                          stroke="var(--chart-cost)"
                          strokeWidth={2.5}
                          dot={false}
                          activeDot={{ r: 5, fill: 'var(--chart-cost)', stroke: 'var(--chart-tooltip-bg)', strokeWidth: 2 }}
                        />
                      </>
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* ======== SEÇÕES FIXAS (visão "Todas") ======== */}
            {isAllView && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Breakdown por Conta WABA */}
                <div className="bg-bg-2 rounded-xl border border-border-1 overflow-hidden">
                  <div className="p-4 border-b border-border-1 flex items-center gap-2">
                    <Phone size={16} className="text-accent-1" />
                    <h3 className="text-sm font-semibold text-text-1">Contas WABA</h3>
                    <span className="ml-auto text-xs text-text-3">{billing.wabaBreakdown.length} conta(s)</span>
                  </div>
                  {billing.wabaBreakdown.length === 0 ? (
                    <p className="text-text-3 text-center py-8 text-sm">Nenhum dado disponível</p>
                  ) : (
                    <div className="overflow-x-auto max-h-80 overflow-y-auto">
                      <table className="w-full">
                        <thead className="bg-bg-3/50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2.5 text-left text-xs font-medium text-text-3 uppercase tracking-wider">Conta</th>
                            <th className="px-4 py-2.5 text-right text-xs font-medium text-text-3 uppercase tracking-wider">Disparos</th>
                            <th className="px-4 py-2.5 text-right text-xs font-medium text-text-3 uppercase tracking-wider">Custo</th>
                            <th className="px-4 py-2.5 text-center text-xs font-medium text-text-3 uppercase tracking-wider"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border-1">
                          {billing.wabaBreakdown.map((waba) => (
                            <tr key={waba.wabaAccountId} className="hover:bg-bg-3/30 transition-colors">
                              <td className="px-4 py-3">
                                <p className="text-sm font-medium text-text-1">{waba.displayPhoneNumber}</p>
                                <p className="text-xs text-text-3">{waba.businessName}</p>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-sm font-medium text-text-1">{formatNumber(waba.totalDispatches)}</span>
                                {waba.freeDispatches > 0 && (
                                  <span className="block text-xs text-success">({waba.freeDispatches} grátis)</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-sm font-semibold text-success">{formatCurrency(waba.totalCost)}</span>
                                <span className="block text-xs text-text-3">U:{formatNumber(waba.utilityCount)} M:{formatNumber(waba.marketingCount)}</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => handleWabaSelect(waba.wabaAccountId)}
                                  className="px-3 py-1.5 text-xs font-medium text-accent-1 bg-accent-1/10 hover:bg-accent-1/20 rounded-md transition-colors"
                                >
                                  Detalhar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Top Usuários */}
                <div className="bg-bg-2 rounded-xl border border-border-1 overflow-hidden">
                  <div className="p-4 border-b border-border-1 flex items-center gap-2">
                    <Users size={16} className="text-accent-2" />
                    <h3 className="text-sm font-semibold text-text-1">Top Usuários</h3>
                    <span className="ml-auto text-xs text-text-3">{billing.topUsers.length} usuário(s)</span>
                  </div>
                  {billing.topUsers.length === 0 ? (
                    <p className="text-text-3 text-center py-8 text-sm">Nenhum dado disponível</p>
                  ) : (
                    <div className="overflow-x-auto max-h-80 overflow-y-auto">
                      <table className="w-full">
                        <thead className="bg-bg-3/50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2.5 text-left text-xs font-medium text-text-3 uppercase tracking-wider">Usuário</th>
                            <th className="px-4 py-2.5 text-right text-xs font-medium text-text-3 uppercase tracking-wider">Disparos</th>
                            <th className="px-4 py-2.5 text-right text-xs font-medium text-text-3 uppercase tracking-wider">Custo</th>
                            <th className="px-4 py-2.5 text-right text-xs font-medium text-text-3 uppercase tracking-wider">%</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border-1">
                          {billing.topUsers.map((u) => (
                            <tr key={u.userId} className="hover:bg-bg-3/30 transition-colors">
                              <td className="px-4 py-3">
                                <p className="text-sm font-medium text-text-1">{u.userName}</p>
                                <p className="text-xs text-text-3">{u.userEmail}</p>
                              </td>
                              <td className="px-4 py-3 text-right text-sm font-medium text-text-1">{formatNumber(u.totalDispatches)}</td>
                              <td className="px-4 py-3 text-right text-sm font-semibold text-text-1">{formatCurrency(u.totalCost)}</td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <div className="w-16 bg-bg-3 rounded-full h-1.5">
                                    <div className="bg-accent-1 h-1.5 rounded-full" style={{ width: `${Math.min(u.percentage, 100)}%` }} />
                                  </div>
                                  <span className="text-sm text-text-2 w-12 text-right">{formatPercentage(u.percentage)}</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ======== TABS DE DETALHES (apenas com WABA selecionada) ======== */}
            {!isAllView && (
              <div className="bg-bg-2 rounded-xl border border-border-1 mb-6 overflow-hidden">
                {/* Tab bar */}
                <div className="flex items-center gap-1 p-1.5 bg-bg-3/50 border-b border-border-1 overflow-x-auto">
                  {tabs.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`
                          flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap
                          ${isActive
                            ? 'bg-bg-1 text-accent-1 shadow-sm'
                            : 'text-text-2 hover:text-text-1 hover:bg-bg-1/50'
                          }
                        `}
                      >
                        {tab.icon}
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {/* Tab content */}
                <div className="p-5">
                  {/* TAB: Contas WABA */}
                  {activeTab === 'waba' && (
                    <div>
                      {billing.wabaBreakdown.length === 0 ? (
                        <p className="text-text-3 text-center py-8">Nenhum dado disponível</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-border-1">
                                <th className="px-4 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">Conta</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">Disparos</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">Utility</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">Marketing</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">Custo</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-text-3 uppercase tracking-wider">Ação</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border-1">
                              {billing.wabaBreakdown.map((waba) => (
                                <tr key={waba.wabaAccountId} className="hover:bg-bg-3/30 transition-colors">
                                  <td className="px-4 py-4">
                                    <p className="text-sm font-medium text-text-1">{waba.displayPhoneNumber}</p>
                                    <p className="text-xs text-text-3">{waba.businessName}</p>
                                  </td>
                                  <td className="px-4 py-4 text-right">
                                    <span className="text-sm font-medium text-text-1">{formatNumber(waba.totalDispatches)}</span>
                                    {waba.freeDispatches > 0 && (
                                      <span className="block text-xs text-success">({waba.freeDispatches} grátis)</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-4 text-right text-sm text-text-2">
                                    {formatNumber(waba.utilityCount)}
                                    <span className="block text-xs text-text-3">{formatCurrency(waba.utilityCost)}</span>
                                  </td>
                                  <td className="px-4 py-4 text-right text-sm text-text-2">
                                    {formatNumber(waba.marketingCount)}
                                    <span className="block text-xs text-text-3">{formatCurrency(waba.marketingCost)}</span>
                                  </td>
                                  <td className="px-4 py-4 text-right">
                                    <span className="text-sm font-semibold text-success">{formatCurrency(waba.totalCost)}</span>
                                  </td>
                                  <td className="px-4 py-4 text-center">
                                    <button
                                      onClick={() => handleWabaSelect(waba.wabaAccountId)}
                                      className="px-3 py-1.5 text-xs font-medium text-accent-1 bg-accent-1/10 hover:bg-accent-1/20 rounded-md transition-colors"
                                    >
                                      Detalhar
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* TAB: Top Templates */}
                  {activeTab === 'templates' && (
                    loadingWaba ? (
                      <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-text-3" /></div>
                    ) : (wabaTopTemplates?.templates?.length ?? 0) > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {wabaTopTemplates!.templates.slice(0, 6).map((template, idx) => (
                            <div key={template.templateId} className="p-4 bg-bg-3/50 rounded-lg border border-border-1">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-text-1 text-sm truncate">{template.templateName}</p>
                                  <p className="text-xs text-text-3 mt-0.5">{template.category}</p>
                                </div>
                                <span className="text-xs font-bold text-accent-1 bg-accent-1/10 px-2 py-0.5 rounded-full ml-2">#{idx + 1}</span>
                              </div>
                              <div className="grid grid-cols-3 gap-2 text-xs mt-3">
                                <div>
                                  <p className="text-text-3">Enviadas</p>
                                  <p className="font-medium text-text-1 mt-0.5">{formatNumber(template.totalSent)}</p>
                                </div>
                                <div>
                                  <p className="text-text-3">Entrega</p>
                                  <p className="font-medium text-success mt-0.5">{formatPercentage(template.deliveryRate)}</p>
                                </div>
                                <div>
                                  <p className="text-text-3">Custo</p>
                                  <p className="font-medium text-text-1 mt-0.5">{formatCurrencyCents(template.totalCost)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-text-3 text-center py-8">Nenhum template com dados no período</p>
                    )
                  )}

                  {/* TAB: Top Usuários */}
                  {activeTab === 'users' && (
                    <div>
                      {billing.topUsers.length === 0 ? (
                        <p className="text-text-3 text-center py-8">Nenhum dado disponível</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-border-1">
                                <th className="px-4 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">Usuário</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">Disparos</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">Custo</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">%</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border-1">
                              {billing.topUsers.map((u) => (
                                <tr key={u.userId} className="hover:bg-bg-3/30 transition-colors">
                                  <td className="px-4 py-4">
                                    <p className="text-sm font-medium text-text-1">{u.userName}</p>
                                    <p className="text-xs text-text-3">{u.userEmail}</p>
                                  </td>
                                  <td className="px-4 py-4 text-right text-sm font-medium text-text-1">{formatNumber(u.totalDispatches)}</td>
                                  <td className="px-4 py-4 text-right text-sm font-semibold text-text-1">{formatCurrency(u.totalCost)}</td>
                                  <td className="px-4 py-4 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <div className="w-16 bg-bg-3 rounded-full h-1.5">
                                        <div className="bg-accent-1 h-1.5 rounded-full" style={{ width: `${Math.min(u.percentage, 100)}%` }} />
                                      </div>
                                      <span className="text-sm text-text-2 w-12 text-right">{formatPercentage(u.percentage)}</span>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* TAB: Erros */}
                  {activeTab === 'errors' && (
                    loadingWaba ? (
                        <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-text-3" /></div>
                      ) : (wabaErrors?.errors?.length ?? 0) > 0 ? (
                        <div>
                          <div className="flex items-center gap-2 mb-4 p-3 bg-danger/5 rounded-lg border border-danger/10">
                            <AlertTriangle size={16} className="text-danger" />
                            <span className="text-sm text-text-2">
                              Total: <span className="font-semibold text-danger">{formatNumber(wabaErrors!.totalErrors)}</span> erros no período
                            </span>
                          </div>
                          <div className="space-y-2">
                            {wabaErrors!.errors.slice(0, 5).map((err) => (
                              <div key={err.errorCode} className="flex items-center justify-between p-3 bg-bg-3/50 rounded-lg border border-border-1">
                                <div className="flex items-center gap-3">
                                  <code className="text-xs font-mono text-danger bg-danger/10 px-2 py-0.5 rounded">{err.errorCode}</code>
                                  <span className="text-sm text-text-2">{err.description}</span>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                  <span className="text-text-1 font-medium">{formatNumber(err.count)}</span>
                                  <span className="text-danger font-medium">{formatPercentage(err.percentage)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center py-8">
                          <CheckCircle2 size={40} className="text-success mb-2" />
                          <p className="text-text-3">Nenhum erro registrado no período</p>
                        </div>
                    )
                  )}

                  {/* TAB: Qualidade */}
                  {activeTab === 'quality' && (
                    loadingWaba ? (
                        <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-text-3" /></div>
                      ) : wabaQuality ? (
                        <div className="space-y-4">
                          {/* Status atual */}
                          <div className="flex items-center justify-between p-4 bg-bg-3/50 rounded-lg border border-border-1">
                            <div className="flex items-center gap-3">
                              <Star size={20} className="text-warning" />
                              <div>
                                <p className="text-xs text-text-3">Qualidade Atual</p>
                                <p className="text-sm font-semibold text-text-1">{wabaQuality.displayPhoneNumber}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getQualityRatingColor(wabaQuality.currentQualityRating)}`}>
                                {getQualityRatingLabel(wabaQuality.currentQualityRating)}
                              </span>
                              <div className="text-right">
                                <p className="text-xs text-text-3">Limite</p>
                                <p className="text-sm font-medium text-text-1">
                                  {wabaQuality.currentMessagingLimit?.replace('TIER_', '').replace('K', 'k')}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Timeline */}
                          {wabaQuality.history && wabaQuality.history.length > 0 ? (
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              <p className="text-xs text-text-3 uppercase tracking-wider mb-2">Histórico</p>
                              {wabaQuality.history.slice(0, 10).map((entry, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-3 bg-bg-3/30 rounded-lg">
                                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    entry.rating === 'GREEN' ? 'bg-success' :
                                    entry.rating === 'YELLOW' ? 'bg-warning' :
                                    entry.rating === 'RED' ? 'bg-danger' : 'bg-text-3'
                                  }`} />
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className={`text-sm font-medium ${getQualityRatingColor(entry.rating)}`}>
                                        {getQualityRatingLabel(entry.rating)}
                                      </span>
                                      <span className="text-xs text-text-3">
                                        {entry.messagingLimit?.replace('TIER_', '').replace('K', 'k')}
                                      </span>
                                    </div>
                                    <p className="text-xs text-text-3 mt-0.5">
                                      {new Date(entry.timestamp).toLocaleString('pt-BR')} &middot; {entry.event}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-text-3 text-center py-4">Nenhuma alteração registrada</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-text-3 text-center py-8">Dados de qualidade indisponíveis</p>
                    )
                  )}
                </div>
              </div>
            )}

            {/* ======== HISTÓRICO DE FATURAS ======== */}
            {billing.invoiceHistory && billing.invoiceHistory.length > 0 && (
              <div className="bg-bg-2 rounded-lg border border-border-1 overflow-hidden">
                <div className="p-4 border-b border-border-1">
                  <h3 className="text-base font-semibold text-text-1 flex items-center gap-2">
                    <FileText size={18} className="text-accent-1" />
                    Histórico de Faturas
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-bg-3/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">Período</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">Disparos</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">Total</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-text-3 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-text-3 uppercase tracking-wider">Ação</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-1">
                      {billing.invoiceHistory.map((invoice) => (
                        <tr key={invoice.id} className="hover:bg-bg-3/30 transition-colors">
                          <td className="px-4 py-3 text-sm text-text-1">
                            {formatPeriod(invoice.periodStart, invoice.periodEnd)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-text-1">
                            {formatNumber(invoice.totalDispatches)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-text-1">
                            {formatCurrency(invoice.totalCost)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[invoice.status]}`}>
                              {statusLabels[invoice.status]}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => fetchInvoiceDetails(invoice.id)}
                              disabled={loadingInvoice}
                              className="p-1.5 text-accent-1 hover:bg-accent-1/10 rounded-md transition-colors disabled:opacity-50"
                              title="Ver detalhes"
                            >
                              <Eye size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!loadingBilling && !billing && !error && (
          <div className="text-center py-20">
            <Phone size={48} className="mx-auto text-text-3 mb-4" />
            <h3 className="text-lg font-medium text-text-1 mb-2">Sem dados disponíveis</h3>
            <p className="text-text-3">Não há estatísticas para exibir.</p>
          </div>
        )}
      </main>

      {/* ======== MODAL: DETALHES DA FATURA ======== */}
      {viewingInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-bg-1">
            <div className="p-6 border-b border-border-1 sticky top-0 bg-bg-1 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-text-1">Detalhes da Fatura</h3>
                  <p className="text-sm text-text-3 mt-1">
                    {formatPeriod(viewingInvoice.periodStart, viewingInvoice.periodEnd)}
                  </p>
                </div>
                <button
                  onClick={() => setViewingInvoice(null)}
                  className="p-2 text-text-3 hover:text-text-1 hover:bg-bg-3 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Resumo */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-bg-3 p-3 rounded-lg">
                  <p className="text-xs text-text-3 uppercase">Valor Total</p>
                  <p className="text-lg font-bold text-success">{formatCurrency(viewingInvoice.totalCost)}</p>
                </div>
                <div className="bg-bg-3 p-3 rounded-lg">
                  <p className="text-xs text-text-3 uppercase">Cobrados</p>
                  <p className="text-lg font-bold text-text-1">
                    {formatNumber(viewingInvoice.utilityCount + viewingInvoice.marketingCount)}
                  </p>
                </div>
                <div className="bg-bg-3 p-3 rounded-lg">
                  <p className="text-xs text-text-3 uppercase">Gratuitos</p>
                  <p className="text-lg font-bold text-text-1">{formatNumber(viewingInvoice.freeDispatches)}</p>
                </div>
                <div className="bg-bg-3 p-3 rounded-lg">
                  <p className="text-xs text-text-3 uppercase">Status</p>
                  <span className={`inline-block mt-1 px-2 py-1 rounded-full text-xs font-medium ${statusColors[viewingInvoice.status]}`}>
                    {statusLabels[viewingInvoice.status]}
                  </span>
                </div>
              </div>

              {/* Breakdown por Categoria */}
              <div>
                <h4 className="text-sm font-semibold text-text-2 mb-3">Por Categoria</h4>
                <div className="bg-bg-3 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-bg-1">
                      <tr>
                        <th className="px-3 py-2 text-left text-text-3">Categoria</th>
                        <th className="px-3 py-2 text-right text-text-3">Qtd</th>
                        <th className="px-3 py-2 text-right text-text-3">Custo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-1">
                      <tr>
                        <td className="px-3 py-2 text-text-1">UTILITY</td>
                        <td className="px-3 py-2 text-right text-text-1">{formatNumber(viewingInvoice.utilityCount)}</td>
                        <td className="px-3 py-2 text-right font-medium text-success">{formatCurrency(viewingInvoice.utilityCost)}</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 text-text-1">MARKETING</td>
                        <td className="px-3 py-2 text-right text-text-1">{formatNumber(viewingInvoice.marketingCount)}</td>
                        <td className="px-3 py-2 text-right font-medium text-success">{formatCurrency(viewingInvoice.marketingCost)}</td>
                      </tr>
                      <tr className="bg-bg-1 font-semibold">
                        <td className="px-3 py-2 text-text-1">TOTAL</td>
                        <td className="px-3 py-2 text-right text-text-1">{formatNumber(viewingInvoice.utilityCount + viewingInvoice.marketingCount)}</td>
                        <td className="px-3 py-2 text-right text-success">{formatCurrency(viewingInvoice.totalCost)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Boleto */}
              {(viewingInvoice.boletoUrl || viewingInvoice.boletoNumber) && (
                <div>
                  <h4 className="text-sm font-semibold text-text-2 mb-2">Boleto</h4>
                  <div className="bg-bg-3 p-4 rounded-lg space-y-2">
                    {viewingInvoice.boletoNumber && <p className="text-sm text-text-1 font-mono">{viewingInvoice.boletoNumber}</p>}
                    {viewingInvoice.boletoUrl && (
                      <a href={viewingInvoice.boletoUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-accent-1 hover:underline">
                        Abrir boleto
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Notas */}
              {viewingInvoice.notes && (
                <div>
                  <h4 className="text-sm font-semibold text-text-2 mb-2">Observações</h4>
                  <p className="text-sm text-text-1 bg-bg-3 p-4 rounded-lg">{viewingInvoice.notes}</p>
                </div>
              )}

              {/* Breakdown por WABA */}
              {viewingInvoice.breakdown?.byWaba && viewingInvoice.breakdown.byWaba.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-text-2 mb-3">Por Conta WABA</h4>
                  <div className="bg-bg-3 rounded-lg overflow-hidden overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-bg-1">
                        <tr>
                          <th className="px-3 py-2 text-left text-text-3">Número</th>
                          <th className="px-3 py-2 text-right text-text-3">UTILITY</th>
                          <th className="px-3 py-2 text-right text-text-3">MARKETING</th>
                          <th className="px-3 py-2 text-right text-text-3">Custo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-1">
                        {viewingInvoice.breakdown.byWaba.map((w, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2 text-text-1">{w.displayPhoneNumber}</td>
                            <td className="px-3 py-2 text-right text-text-1">{formatNumber(w.utilityCount || 0)}</td>
                            <td className="px-3 py-2 text-right text-text-1">{formatNumber(w.marketingCount || 0)}</td>
                            <td className="px-3 py-2 text-right font-medium text-success">{formatCurrency(w.totalCost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
